import {
  Alert,
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Skeleton,
  Text
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import type {
  QueueListState,
  TransferDestination
} from '../../interface/transfer';
import { CASE_ACTION_RADIUS, TRANSFER_COLORS } from '../../styles/tokens';

export interface RecipientComboboxProps {
  readonly id?: string;
  readonly labelledBy?: string;
  readonly initialRecipients?: readonly TransferDestination[];
  readonly onSearch?: (term: string) => Promise<readonly TransferDestination[]>;
  readonly value?: TransferDestination;
  readonly onChange: (value: TransferDestination | undefined) => void;
  readonly queues: QueueListState;
  readonly onRetryQueues: () => void;
  readonly isDisabled?: boolean;
  readonly isInvalid?: boolean;
  readonly errorId?: string;
  readonly debounceMs?: number;
  readonly inputId?: string;
}

// The ARIA 1.2 combobox keeps focus on the input: open Escape closes only this list,
// closed Escape bubbles to its dialog, and async results update the announced options.
const LISTBOX_ID = 'transfer-recipient-listbox';
const optionId = (index: number): string =>
  `transfer-recipient-option-${index}`;
const keyOf = (destination: TransferDestination): string =>
  `${destination.kind}:${destination.id}`;

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';

const countLabel = (count: number | undefined, noun: string): string =>
  typeof count === 'number'
    ? `${count} ${count === 1 ? noun : `${noun}s`}`
    : '';

const availabilityLabel = (availability: string): string => {
  if (availability === 'OUT_OF_OFFICE') return 'Out of office';
  if (availability === 'UNAVAILABLE') return 'Unavailable';
  const label = availability.replaceAll('_', ' ').toLowerCase();
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const QueueGlyph: React.FC<{ readonly size?: string }> = ({
  size = '15px'
}) => (
  <Box
    as="svg"
    data-testid="transfer-recipient-queue-glyph"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
  </Box>
);

export const RecipientCombobox: React.FC<RecipientComboboxProps> = ({
  id,
  labelledBy,
  initialRecipients,
  onSearch,
  value,
  onChange,
  queues,
  onRetryQueues,
  isDisabled,
  isInvalid,
  errorId,
  debounceMs = 250,
  inputId = 'transfer-recipient-input'
}) => {
  const [text, setText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<readonly TransferDestination[]>(
    initialRecipients ?? []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Each request captures its sequence; only the latest response may replace results.
  const seqRef = useRef(0);
  const focusAfterClearRef = useRef(false);
  const onSearchRef = useRef(onSearch);
  // Keep the callback current without making query scheduling depend on its identity.
  onSearchRef.current = onSearch;

  const query = text.trim();
  const hasSearch = Boolean(onSearch);
  const selected = value;
  const people = results.filter(
    (
      destination
    ): destination is Extract<TransferDestination, { kind: 'user' }> =>
      destination.kind === 'user'
  );
  const queueCandidates =
    queues.status === 'ready'
      ? queues.queues.filter(
          (
            destination
          ): destination is Extract<TransferDestination, { kind: 'queue' }> =>
            destination.kind === 'queue'
        )
      : [];
  const filteredQueues = queueCandidates.filter((queue) =>
    queue.name.toLowerCase().includes(query.toLowerCase())
  );
  const options: readonly TransferDestination[] = [
    ...people,
    ...filteredQueues
  ];
  // The active row follows its key through result changes; otherwise the first row of either group.
  const keyedIndex = activeKey
    ? options.findIndex((option) => keyOf(option) === activeKey)
    : -1;
  const activeIndex =
    keyedIndex >= 0 ? keyedIndex : options.length > 0 ? 0 : -1;

  useEffect(() => {
    if (activeKey && keyedIndex < 0) setActiveKey(undefined);
  }, [activeKey, keyedIndex]);

  useEffect(() => {
    if (query.length === 0 && (!hasSearch || !isOpen)) {
      seqRef.current += 1;
      setResults(initialRecipients ?? []);
      setIsLoading(false);
      return undefined;
    }
    if (!hasSearch) return undefined;
    const seq = ++seqRef.current;
    setIsLoading(true);
    const handle = setTimeout(() => {
      const search = onSearchRef.current;
      if (!search) return;
      search(query)
        .then((found) => {
          if (seq !== seqRef.current) return;
          setResults(found);
          setIsLoading(false);
        })
        .catch(() => {
          if (seq !== seqRef.current) return;
          setResults([]);
          setIsLoading(false);
        });
    }, debounceMs);
    return () => clearTimeout(handle);
  }, [query, hasSearch, isOpen, debounceMs, initialRecipients]);

  useEffect(() => {
    // Focus remains on the input while the active option is scrolled into view for ARIA.
    if (!isOpen || activeIndex < 0) return;
    const element = listRef.current?.querySelector(`#${optionId(activeIndex)}`);
    (element as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  useEffect(() => {
    if (!selected && focusAfterClearRef.current) {
      focusAfterClearRef.current = false;
      inputRef.current?.focus();
    }
  }, [selected]);

  const commit = (destination: TransferDestination): void => {
    onChange(destination);
    setText(destination.name);
    setIsOpen(false);
    setActiveKey(undefined);
  };

  const remove = (): void => {
    focusAfterClearRef.current = true;
    onChange(undefined);
    setText('');
    setActiveKey(undefined);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      if (options.length === 0) return;
      const next =
        event.key === 'ArrowDown'
          ? (activeIndex + 1) % options.length
          : (activeIndex - 1 + options.length) % options.length;
      setActiveKey(keyOf(options[next]));
      return;
    }
    if (event.key === 'Enter' && isOpen && activeIndex >= 0) {
      const destination = options[activeIndex];
      if (destination) {
        event.preventDefault();
        commit(destination);
      }
      return;
    }
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
      setActiveKey(undefined);
    }
  };

  const resultCount = people.length + filteredQueues.length;
  const status = !onSearch
    ? 'Recipient search is unavailable.'
    : isLoading || (query.length > 0 && queues.status === 'loading')
      ? 'Searching…'
      : query.length === 0
        ? ''
        : resultCount === 0
          ? 'No matches'
          : `${resultCount} ${resultCount === 1 ? 'result' : 'results'}`;

  if (selected) {
    return (
      <Box id={id} aria-labelledby={labelledBy}>
        <Flex align="center" gap={2}>
          <Flex
            aria-hidden
            align="center"
            justify="center"
            bg={TRANSFER_COLORS.iconBg}
            color={TRANSFER_COLORS.iconArrow}
            borderRadius="full"
            px={2}
            py={1}
            minW="2rem"
            minH="2rem"
          >
            {selected.kind === 'queue' ? (
              <QueueGlyph />
            ) : (
              initialsOf(selected.name)
            )}
          </Flex>
          <Text flex="1" textStyle="Body 1" color={TRANSFER_COLORS.ink}>
            {selected.name}
          </Text>
          <IconButton
            aria-label="Remove recipient"
            data-testid="transfer-recipient-clear"
            icon={<Box as="span">×</Box>}
            onClick={remove}
            isDisabled={isDisabled}
            variant="ghost"
            size="sm"
          />
        </Flex>
        <Text mt={1} textStyle="Body 2" color={TRANSFER_COLORS.inkFaint}>
          Click × to change recipient.
        </Text>
      </Box>
    );
  }

  return (
    <Box id={id} position="relative">
      <Input
        id={inputId}
        ref={inputRef}
        role="combobox"
        aria-labelledby={labelledBy}
        aria-expanded={isOpen}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={
          isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined
        }
        // Chakra's isInvalid supplies the visual/error semantics; aria-describedby links its message.
        isInvalid={isInvalid}
        aria-describedby={isInvalid && errorId ? errorId : undefined}
        data-testid="transfer-recipient-input"
        autoComplete="off"
        isDisabled={isDisabled}
        placeholder="Person or queue…"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={onKeyDown}
        bg={TRANSFER_COLORS.inputBg}
        borderColor={isInvalid ? TRANSFER_COLORS.error : TRANSFER_COLORS.border}
        borderRadius={CASE_ACTION_RADIUS.control}
        textStyle="Body 1"
      />
      <Text
        role="status"
        aria-live="polite"
        data-testid="transfer-recipient-status"
        textStyle="Body 1"
        color={TRANSFER_COLORS.inkFaint}
        mt={1}
      >
        {status}
      </Text>
      {isOpen && (
        <Box
          ref={listRef}
          id={LISTBOX_ID}
          role="listbox"
          aria-label="People and queues"
          data-testid="transfer-recipient-listbox"
          // The overlay must not reflow the content below the combobox.
          position="absolute"
          zIndex={10}
          left={0}
          right={0}
          mt={1}
          maxH="16rem"
          overflowY="auto"
          bg="white"
          border={`1px solid ${TRANSFER_COLORS.border}`}
          borderRadius={CASE_ACTION_RADIUS.control}
          boxShadow="md"
        >
          {people.length > 0 && (
            <Box
              role="presentation"
              bg={TRANSFER_COLORS.groupHeaderBg}
              px={3}
              py={1}
              textStyle="Body 1"
              color={TRANSFER_COLORS.inkMuted}
            >
              PEOPLE · {people.length}
            </Box>
          )}
          {people.map((person, index) => (
            <Flex
              key={person.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              data-testid={`transfer-recipient-option-${person.id}`}
              align="center"
              gap={3}
              px={3}
              py={2}
              cursor="pointer"
              bg={
                index === activeIndex ? TRANSFER_COLORS.optionActiveBg : 'white'
              }
              onMouseDown={(event) => {
                // Commit before blur so the input does not close the list before selection.
                event.preventDefault();
                commit(person);
              }}
              onMouseEnter={() => setActiveKey(keyOf(person))}
            >
              <Flex
                aria-hidden
                align="center"
                justify="center"
                flexShrink={0}
                boxSize="2rem"
                borderRadius="full"
                bg={TRANSFER_COLORS.iconBg}
                color={TRANSFER_COLORS.iconArrow}
                textStyle="Body 1"
              >
                {initialsOf(person.name)}
              </Flex>
              <Box minW={0} flex="1">
                <Text
                  textStyle="Body 1"
                  color={TRANSFER_COLORS.ink}
                  fontWeight={600}
                >
                  {person.name}
                </Text>
                {person.role && (
                  <Text textStyle="Body 1" color={TRANSFER_COLORS.inkMuted}>
                    {person.role}
                  </Text>
                )}
              </Box>
              {typeof person.queueCount === 'number' && (
                <Text textStyle="Body 1" color={TRANSFER_COLORS.inkMuted}>
                  {countLabel(person.queueCount, 'queue')}
                </Text>
              )}
              {person.availability && person.availability !== 'AVAILABLE' && (
                <Box
                  bg={TRANSFER_COLORS.availabilityBg}
                  color={TRANSFER_COLORS.availabilityFg}
                  borderRadius={CASE_ACTION_RADIUS.small}
                  px="6px"
                  textStyle="Body 2"
                >
                  {availabilityLabel(person.availability)}
                </Box>
              )}
            </Flex>
          ))}
          {(filteredQueues.length > 0 || queues.status !== 'ready') && (
            <Box
              role="presentation"
              bg={TRANSFER_COLORS.groupHeaderBg}
              px={3}
              py={1}
              textStyle="Body 1"
              color={TRANSFER_COLORS.inkMuted}
            >
              QUEUES · {filteredQueues.length}
            </Box>
          )}
          {queues.status === 'loading' && (
            <Skeleton mx={3} my={2} height="2rem" />
          )}
          {queues.status === 'error' && (
            <Alert status="error" mx={3} my={2}>
              <Box>
                {queues.message}
                <Button variant="link" onClick={onRetryQueues} ml={2}>
                  Try again
                </Button>
              </Box>
            </Alert>
          )}
          {filteredQueues.map((queue, offset) => {
            const index = people.length + offset;
            return (
              <Flex
                key={queue.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                data-testid={`transfer-recipient-option-${queue.id}`}
                align="center"
                gap={3}
                px={3}
                py={2}
                cursor="pointer"
                bg={
                  index === activeIndex
                    ? TRANSFER_COLORS.optionActiveBg
                    : 'white'
                }
                onMouseDown={(event) => {
                  // Commit before blur so the input does not close the list before selection.
                  event.preventDefault();
                  commit(queue);
                }}
                onMouseEnter={() => setActiveKey(keyOf(queue))}
              >
                <Flex
                  aria-hidden
                  align="center"
                  justify="center"
                  flexShrink={0}
                  boxSize="2rem"
                  borderRadius="full"
                  bg={TRANSFER_COLORS.iconBg}
                  color={TRANSFER_COLORS.iconArrow}
                >
                  <QueueGlyph />
                </Flex>
                <Box minW={0} flex="1">
                  <Text
                    textStyle="Body 1"
                    color={TRANSFER_COLORS.ink}
                    fontWeight={600}
                  >
                    {queue.name}
                  </Text>
                  {queue.description && (
                    <Text textStyle="Body 1" color={TRANSFER_COLORS.inkMuted}>
                      {queue.description}
                    </Text>
                  )}
                </Box>
                {typeof queue.caseCount === 'number' && (
                  <Text textStyle="Body 1" color={TRANSFER_COLORS.inkMuted}>
                    {countLabel(queue.caseCount, 'case')}
                  </Text>
                )}
              </Flex>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
